import {
  ariaAttributes,
  setTabIndexes,
  accessibleSubMenus,
} from '../helpers/accessibility';

import { waitForElement, useChaoticNav } from '../helpers/helpers';
import { fireAdobeAnalyticsEvent } from '../helpers/adobe-analytics';

const navSelectedEvent = new Event('nav-item-selected', {
    bubbles: true,
    composed: true,
  }),
  resetMobileNav = new Event('reset-mobile-nav', {
    bubbles: true,
    composed: true,
  }),
  main = document.querySelector('main'),
  globalNav = document.getElementById('js-new-global-nav'),
  topNav = document.querySelector('.main-nav nav'),
  footer = document.querySelector('global-footer'),
  navPanel = document.querySelector('nav-panel'),
  globalSearch = document.querySelector('global-search'),
  globalSearchTrigger = document.querySelector('#js-new-nav-search-button'),
  parentListItems = Array.from(
    document.querySelectorAll('.js-parent-list-item')
  ),
  mobileNavToggles = Array.from(
    document.querySelectorAll('#js-toggle-mobile-nav')
  ),
  showClass = 'show',
  dimmedClass = 'dimmed',
  fallbackMobileMenuTop = 75,
  fallbackDesktopMenuTop = 122;

const init = async function() {
  if (globalNav) {
    ariaAttributes();
    accessibleSubMenus();
    mobileNavToggleEventListeners();
    parentListItemEventListeners();
    requestQuoteEventListener();
    setupAnalyticsListener();
    // Used only as fun prank
    if (useChaoticNav()) {
      implementChaoticNav();
    }
  }
  if (globalSearch) addGlobalSearchEventListener();
  if (window.useMobileLayout || window.useTabletLayout) {
    const topNavigation = await waitForElement('.main-nav nav'),
      navigationPanel = await waitForElement('nav-panel'),
      globalSearchElement = await waitForElement('global-search');
    if (topNavigation && navigationPanel && globalSearchElement) {
      // add default value if not found (safari specific)
      const bottomOfTopNav = navPanelTopPosition();
      navigationPanel.style.top = `${bottomOfTopNav}px`;
      globalSearchElement.style.top = `${bottomOfTopNav}px`;
    } else {
      console.warn(
        'Missed an element:',
        topNavigation,
        navigationPanel,
        globalSearchElement
      );
    }
  }
};

const addGlobalSearchEventListener = function() {
  globalSearchTrigger.addEventListener('click', toggleSearchPanelDisplay);
};

const mobileNavToggleEventListeners = function() {
  mobileNavToggles.forEach(mobileToggle => {
    mobileToggle.addEventListener('click', function() {
      // switch the open menu svg to the close svg
      mobileNavToggles.forEach(toggleElement => {
        if (toggleElement.classList.contains('mobile-nav-menu')) {
          navPanel.dispatchEvent(resetMobileNav);
        }
        toggleElement.classList.toggle('hidden');
      });

      toggleNavPanelDisplay();
    });
  });
};

const requestQuoteEventListener = function() {
  const requestQuoteElement = document.querySelector('.js-quote-request');
  if (requestQuoteElement) {
    requestQuoteElement.addEventListener('click', function() {
      window.sessionStorage.removeItem('requestInfoData');
    });
  }
};

const setupAnalyticsListener = function() {
  const navLinks = document.querySelectorAll(
    '#js-new-global-nav a[data-nav-label]'
  );

  navLinks.forEach(link => {
    link.addEventListener('click', function(event) {
      const label = link.getAttribute('data-nav-label');
      const href = link.getAttribute('href');
      const useNewTab = link.target === '_blank';

      // Override default navigation
      event.preventDefault();
      sendNavEvent(label);
      setTimeout(() => {
        if (useNewTab) {
          window.open(href, '_blank');
        } else {
          window.location.href = href;
        }
      }, 100); // Slight delay for tracking to go through
    });
  });

  function sendNavEvent(label) {
    let navType = label.toLowerCase().includes('lightart')
      ? 'EXIT'
      : label.toLowerCase().includes('find a consultant')
      ? 'INP'
      : 'NAV';
    const eventData = {
      type: 'click',
      detail: {
        eVar8: `${navType}|Top Nav Header|${label}`,
      },
    };
    fireAdobeAnalyticsEvent(eventData);
  }
};

const toggleNavPanelDisplay = function() {
  if (globalSearch.classList.contains(showClass)) {
    toggleSearchPanelDisplay();
  }

  navPanel.classList.toggle(showClass);
  toggleSharedClasses();

  if (main.style.top === '') {
    main.style.top = `${navPanelTopPosition()}px`;
    navPanel.style.top = `${navPanelTopPosition()}px`;
    main.addEventListener('click', toggleNavPanelDisplay);
    document.addEventListener('keyup', escapeKeyNavClose);
  } else {
    main.style.top = '';
    navPanel.style.top = '';
    main.removeEventListener('click', toggleNavPanelDisplay);
    document.removeEventListener('keyup', escapeKeyNavClose);
  }
};

const navPanelTopPosition = function() {
  let topPosition;
  const bottomOfTopNav = document
    .getElementById('js-global-nav')
    .getBoundingClientRect().bottom;
  const topOfMain = document.querySelector('main').getBoundingClientRect().top;
  if (!!bottomOfTopNav && bottomOfTopNav > 0) {
    topPosition = bottomOfTopNav;
  } else if (!!topOfMain && topOfMain > 0) {
    topPosition = topOfMain;
  }
  if (!topPosition || topPosition < 0) {
    topPosition =
      window.useMobileLayout || window.useTabletLayout
        ? fallbackMobileMenuTop
        : fallbackDesktopMenuTop;
  }
  return topPosition;
};

const toggleSearchPanelDisplay = function(event) {
  if (window.useMobileLayout || window.useTabletLayout) {
    // on mobile the search panel is in the nav's first panel
    toggleNavPanelDisplay();
    return;
  }

  if (navPanel.classList.contains(showClass)) {
    toggleNavPanelDisplay();
  }

  globalSearch.classList.toggle(showClass);
  toggleSharedClasses();

  if (main.style.top === '') {
    main.style.top = `${navPanelTopPosition()}px`;
    main.addEventListener('click', toggleSearchPanelDisplay);
    globalSearch.focus();
  } else {
    main.style.top = '';
    main.removeEventListener('click', toggleSearchPanelDisplay);
  }
  if (event) {
    event.preventDefault();
  }
};

const toggleSharedClasses = function() {
  main.classList.toggle('no-scroll');
  footer.classList.toggle('hidden');
  if (
    (window.useMobileLayout || window.useTabletLayout) &&
    !main.classList.contains(dimmedClass)
  ) {
    return;
  }
  main.classList.toggle(dimmedClass);
};

const escapeKeyNavClose = function(event) {
  if (event.key === 'Escape') {
    toggleNavPanelDisplay();
  }
};

const parentListItemEventListeners = function() {
  parentListItems.forEach(parentListItem => {
    parentListItem
      .querySelector('a')
      .addEventListener('click', function(_event) {
        if (
          // prettier-ignore
          (
            navPanel.getAttribute('currentpanel') ===
              parentListItem.getAttribute('data-content') &&
            navPanel.classList.contains(showClass)
          ) ||
          !navPanel.classList.contains(showClass)
        ) {
          toggleNavPanelDisplay();
        }

        navPanel.setAttribute(
          'currentpanel',
          parentListItem.getAttribute('data-content')
        );
        navPanel.dispatchEvent(navSelectedEvent);
        parentListItem.classList.add('is-active');
        navPanel.scrollTop = 0;
        setTabIndexes(
          parentListItem.querySelectorAll('.js-child-list-item'),
          '0'
        );
      });
  });
};

const implementChaoticNav = () => {
  const ul = document.querySelector('ul.center');

  // Insert these links between the real ones
  const links = [
    { name: 'Digital Print', url: '/digital-printing-portfolio' },
    { name: 'Graphic Patterns', url: '/graphic-pattern-portfolio' },
    { name: 'Naturals', url: '/biophilic-graphic-patterns' },
    { name: 'Diffusions', url: '/whites-diffusions-effects' },
    { name: 'View All', url: '/', position: 'last', color: '#ff993d' },
  ];

  const originalItems = ul ? Array.from(ul.children) : [];

  function createListItem({ name, url, color }) {
    const li = document.createElement('li');
    li.className = 'main-nav__parent-list-item has-aria js-parent-list-item';
    li.setAttribute('data-content', name);

    const a = document.createElement('a');
    a.setAttribute('aria_expanded', 'false');
    a.setAttribute('aria_haspopup', 'true');
    a.setAttribute('tabindex', '-1');
    a.setAttribute('href', url);
    a.textContent = name;

    if (color) {
      a.style.color = color;
    }

    li.appendChild(a);
    return li;
  }

  if (ul) {
    const lastLinks = links.filter(link => link.position === 'last');
    const inBetweenLinks = links.filter(link => !link.position);

    // Insert in-between links evenly between original items
    const insertPoints = originalItems.length - 1;
    const numToInsert = inBetweenLinks.length;

    inBetweenLinks.forEach((link, i) => {
      const li = createListItem(link);
      const targetIndex = Math.round(
        (i + 1) * (insertPoints / (numToInsert + 1))
      );
      const refNode = originalItems[targetIndex] || null;
      ul.insertBefore(li, refNode);
    });

    const lastOriginal = originalItems[originalItems.length - 1];
    let lastInsertRef = lastOriginal?.nextSibling || null;

    lastLinks.forEach(link => {
      const li = createListItem(link);
      ul.insertBefore(li, lastInsertRef);
    });
  }
};

export default {
  init,
};
